/**
 * QAGenerator — Fiches Q&R PDF (Carbon Design System)
 */

import { useState } from 'react';
import { Chat } from '@carbon/icons-react';
import {
  FileUpload,
  LanguageSelector,
  AIProcessingCard,
  AiGeneratorPageLayout,
  AiGeneratorFormCard,
  AiFormStep,
  AiGenerateButton,
  AiSuccessBanner,
} from '@sphere/learning/ui';
import { useGenerateQAPdf, useFileValidation } from '@sphere/learning/data-access';
import type { SupportedLanguage } from '@sphere/ai/domain';
import { handleAIError } from '@sphere/ai/data-access';
import { downloadBlob } from '@sphere/shared/util';
import tw from './QAGenerator.tailwind.module.scss';

const PDF_CONTENT = [
  'Questions sur les concepts clés',
  'Réponses détaillées et structurées',
  'Organisé par sections thématiques',
  'Mise en page professionnelle et claire',
];

export default function QAGenerator() {
  const [language, setLanguage] = useState<SupportedLanguage>('fr' as SupportedLanguage);
  const [isSuccess, setIsSuccess] = useState(false);
  const [lastFileName, setLastFileName] = useState<string>('');
  const [lastBlob, setLastBlob] = useState<Blob | null>(null);

  const { file, validation, setFile } = useFileValidation();
  const generateQAPdf = useGenerateQAPdf();

  const handleGenerate = async () => {
    if (!file || !validation.isValid) return;
    try {
      const blob = await generateQAPdf.mutateAsync({ file, lang: language });
      const filename = `${file.name.split('.')[0]}-qa.pdf`;
      downloadBlob(blob, filename);
      setLastBlob(blob);
      setLastFileName(filename);
      setIsSuccess(true);
    } catch (error) {
      console.error('Q&A PDF generation failed:', error);
    }
  };

  const handleDownload = () => {
    if (lastBlob && lastFileName) {
      downloadBlob(lastBlob, lastFileName);
    }
  };

  const handleNewGeneration = () => {
    setFile(null);
    setIsSuccess(false);
    setLastFileName('');
    setLastBlob(null);
  };

  return (
    <AiGeneratorPageLayout
      icon={Chat}
      title="Générateur de Fiches Q&R"
      description="Créez des fiches questions/réponses complètes à partir de vos documents — un moyen professionnel d'étudier et de créer du matériel pédagogique."
    >
        {isSuccess && (
          <AiSuccessBanner
            title="Fiches Q&R générées avec succès"
            fileName={lastFileName}
            onDownload={handleDownload}
            onNewGeneration={handleNewGeneration}
          />
        )}

        <AiGeneratorFormCard title="Générer PDF de Q&R" icon={Chat}>
          <AiFormStep step={1} label="Sélectionnez votre document">
            <FileUpload
              file={file}
              onFileChange={setFile}
              disabled={generateQAPdf.isPending}
            />
            {validation.error && (
              <p className={`cds--form-requirement ${tw.u1}`}>{validation.error}</p>
            )}
          </AiFormStep>
          <AiFormStep step={2} label="Choisissez la langue">
            <LanguageSelector
              value={language}
              onChange={setLanguage}
              disabled={generateQAPdf.isPending}
            />
          </AiFormStep>
          <AiFormStep step={3} label="Contenu du PDF">
            <ul className={`cds--type-body-compact-01 ${tw.u2}`}>
              {PDF_CONTENT.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </AiFormStep>
          <AiFormStep step={4} label="Générez et téléchargez">
            <AiGenerateButton
              onClick={handleGenerate}
              disabled={
                !file || !validation.isValid || generateQAPdf.isPending
              }
              isLoading={generateQAPdf.isPending}
              label="Générer et Télécharger le PDF"
              loadingLabel="Génération..."
              icon={Chat}
            />
          </AiFormStep>
        </AiGeneratorFormCard>

        {generateQAPdf.isPending && (
          <div className={tw.u3}>
            <AIProcessingCard
              isLoading
              error={null}
              title="Génération des fiches Q&R..."
              description="L'IA analyse votre document et génère des questions pertinentes..."
            />
          </div>
        )}

        {generateQAPdf.error && (
          <div className={tw.u3}>
            <AIProcessingCard
              isLoading={false}
              error={handleAIError(generateQAPdf.error)}
              title="Erreur de génération"
              onRetry={handleGenerate}
            />
          </div>
        )}
    </AiGeneratorPageLayout>
  );
}
